import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Back/forward navigation restores the page as it was left: after following a
   * link out of the drawer, going back landed on the drawer still open over a
   * scroll-locked page. Close it (and any open submenu) instantly.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted || !this.isOpen) return;

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]').forEach(reset);
    reset(this.refs.details);
    removeTrapFocus();
    // The capture-phase `toggle` listener in dialog.js normally clears this when
    // the details closes; clear it now rather than waiting on that event.
    document.documentElement.removeAttribute('scroll-lock');
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // Close one layer per press: the innermost open submenu first, then the drawer itself.
    // Focus can sit outside the submenu (the focus trap returns it to the drawer), so don't rely on the event target.
    const openDetails = [...this.querySelectorAll('details:not(accordion-custom > details)[open]')];
    const innermost = openDetails.at(-1);
    this.#close(innermost instanceof HTMLDetailsElement ? innermost : this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    // Skip details that are already closed (e.g. the submenu that just handed focus back to its own summary).
    let details = event.target.closest('details');
    while (details && !details.hasAttribute('open')) {
      details = details.parentElement?.closest('details') ?? null;
    }

    return details ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    // A tapped summary's click handler runs before the browser flips its
    // <details> open, so #getDetailsElement (which skips closed ones) would
    // hand back the parent drawer and the submenu never got its `menu-open`
    // class: it took three taps to show. Use the summary's own details.
    const tappedSummary = event?.target instanceof Element ? event.target.closest('summary') : null;
    const details =
      tappedSummary?.parentElement instanceof HTMLDetailsElement
        ? tappedSummary.parentElement
        : this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#loadDeferredMenu();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        trapFocus(details);
        // Move focus into the panel that just opened (close/back button first).
        const panel = drawer || details;
        const candidates = panel.querySelectorAll('a[href], button:enabled, summary');
        for (const el of candidates) {
          if (!(el instanceof HTMLElement) || el === summary) continue;
          el.focus({ preventScroll: true });
          if (document.activeElement === el) break;
        }
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  /**
   * The menu list isn't in the page HTML; it arrives with the header's
   * hydration pass, which normally lands right after load. If the drawer is
   * opened before then, ask for it now rather than waiting on the idle pass
   * (the list shows a spinner meanwhile). Shares the in-flight request with
   * that pass, so this never doubles up.
   */
  #loadDeferredMenu() {
    if (!this.querySelector('.menu-drawer__menu[data-deferred]')) return;

    const sectionId = this.closest('.shopify-section')?.id;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    // The menus only depend on the path, so drop the whole query: one cacheable
    // render per path instead of one per filter/sort/variant/page combination
    // (keeping a theme preview's id, so the preview's own header comes back).
    const previewThemeId = url.searchParams.get('preview_theme_id');
    url.search = '';
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    import('@theme/section-hydration')
      .then(({ hydrateNow }) => hydrateNow(sectionId, url, 'drawer'))
      .then(() => this.#setupAnimatedElementListeners())
      .catch(() => {});
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Only restore focus if it is inside the panel being closed (or lost to body).
    const active = document.activeElement;
    const restoreFocus = !active || active === document.body || details.contains(active);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
        // Return focus to the control that opened this panel.
        if (restoreFocus) summary.focus({ preventScroll: true });
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
